import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Dependent } from "domain/entities/dependent";
import { Message } from "domain/entities/message";
import { DependentInputFilter } from "domain/contracts/dependent.contract";
import { Logger } from "infra/logger/logger";
import dependentRepository from "infra/repositories/dependent-repository";

export const useDependentsByFilter = (filter: DependentInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["dependents", filter],
    queryFn: async () => {
      try {
        const result = await dependentRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: !!filter.employeeId,
  });
};

export const useDependentById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["dependent", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Id field invalid");
        const result = await dependentRepository.getById(id);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_DEFAULT) || 0,
  });
};

export const useCreateDependentMutation = () => {
  const queryClient = useQueryClient();
  const logger = Logger.getInstance();

  return useMutation<Message<Dependent>, AxiosError<any>, Dependent>({
    mutationFn: async (dependent: Dependent) => {
      try {
        const result = await dependentRepository.create(dependent);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dependents"] });
    },
  });
};

export const useUpdateDependentMutation = () => {
  const queryClient = useQueryClient();
  const logger = Logger.getInstance();

  return useMutation<Message<boolean>, AxiosError<any>, Dependent>({
    mutationFn: async (dependent: Dependent) => {
      try {
        const result = await dependentRepository.update(dependent);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dependents"] });
      queryClient.invalidateQueries({ queryKey: ["dependent"] });
    },
  });
};

export const useDeleteDependentMutation = () => {
  const queryClient = useQueryClient();
  const logger = Logger.getInstance();

  return useMutation<void, AxiosError<any>, string>({
    mutationFn: async (id: string) => {
      try {
        await dependentRepository.delete(id);
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dependents"] });
    },
  });
};
